/**
 * Param Store
 *
 * One place that knows the current Paramate param table and who is listening to
 * it. <as-configurator> calls publishParams() every time the table changes —
 * after a configure, after a restore, after a project load, after a file
 * upload. Everything reactive hangs off that:
 *
 *   params.subscribe()    — react to one param, a named set, or an alias object
 *   params.subscribeAll() — react to every publish, with the whole table
 *   bindParams()          — the same thing wired straight onto an element's
 *                           attrs, classes and methods (see components/base.ts)
 *
 * Callbacks get the param OBJECT, not just its value, so `.value`, `.min`,
 * `.max` and `.used` all come off the same thing:
 *
 *   params.subscribe('main:width', (p) => setRange(p.min, p.max));
 *
 * Two things this deliberately isn't:
 *
 * - A write path. setParamValue() on <as-configurator> stays the only way to
 *   change a param. It owns pending state, undo history and the input registry
 *   (`trcad-param`), and a second writer would fight all three.
 * - A live view of local edits. What lands here is the table as the server
 *   returned it. A value staged with setParamValue(..., false) shows up once the
 *   configure completes — read getParamValue() on the configurator if you need
 *   the pending value before then.
 */

import { createSource } from './subscriptions.ts';
import { sendEv } from '../data/events.ts';

/** One entry of the Paramate param table. The listed fields are the ones we
 *  read across the library; the index signature keeps everything else Paramate
 *  sends reachable without us having to track it. */
export interface ParamData {
  name?: string;
  descr?: string;
  type?: string;
  /** For `type: 'array'` — the type of one element, e.g. 'file'. */
  elementType?: string;
  value?: any;
  default?: any;
  used?: boolean;
  visible?: boolean;
  style?: string;
  options?: Array<{ value: string | number; text?: string }>;
  min?: number;
  max?: number;
  [field: string]: any;
}

/** The alias-object form: { localName: 'pkg:param' } in, { localName: param } out. */
export type ParamAliases = Record<string, string>;
export type ResolvedParams<T extends ParamAliases> = { [K in keyof T]: ParamData | undefined };

let _params: Record<string, ParamData> = {};
/** Per-param contents at the last publish, for the change check below. */
const _signatures = new Map<string, string>();
let _unserialisableSeq = 0;

/** Subscriber bookkeeping and the three call shapes — see store/subscriptions.ts.
 *  A target is either one param name or an alias object; the whole-table form
 *  is handled by the source itself. */
const _source = createSource<Record<string, ParamData>>({
  name: 'PARAMS',
  snapshot: () => _params,
  resolve: (target) =>
    typeof target === 'string' ? _get(target)
    : Array.isArray(target) ? target.map(_get)
    : _resolve(target as ParamAliases),
  keysOf: (target) =>
    typeof target === 'string' ? [target.trim()]
    : Array.isArray(target) ? target.map(n => n.trim())
    : Object.values(target as ParamAliases).map(n => n.trim()),
});

// ============================================================================
// Internals
// ============================================================================

/** 'width' and 'main:width' name the same param, and both spellings are in use
 *  across apps. Subscriptions can use either, so every notify goes out under
 *  both — an unsubscribed key costs nothing (see createKeyedNotifier). */
function _aliasKeys(key: string): string[] {
  return key.startsWith('main:') ? [key, key.slice(5)] : [key, `main:${key}`];
}

/** "Did anything in this param change" as a string. Paramate hands back fresh
 *  objects on every configure, so identity tells us nothing — the contents have
 *  to be compared. A param that won't stringify (a back-reference into the SDK)
 *  counts as always-changed: firing too often is recoverable, never firing is
 *  the bug nobody can find. */
function _signature(param: ParamData | undefined): string {
  try {
    return JSON.stringify(param) ?? 'undefined';
  } catch {
    return `<unserialisable ${_unserialisableSeq++}>`;
  }
}

// ============================================================================
// Reading
// ============================================================================

/** One param out of any param table, by name with or without the 'main:'
 *  prefix. The one place that spelling rule lives: `params.get` reads the
 *  store through it, and the configurator and inputs read the SDK's own map
 *  through it. */
export function lookupParam<T = ParamData>(table: Record<string, T> | null | undefined, name: string): T | undefined {
  if (!table) return undefined;
  const trimmed = (name ?? '').trim();
  const full = trimmed.startsWith('main:') ? trimmed : `main:${trimmed}`;
  const short = trimmed.startsWith('main:') ? trimmed.slice(5) : trimmed;
  return table[trimmed] ?? table[full] ?? table[short];
}

/** One param by name, with or without the 'main:' prefix. */
function _get(name: string): ParamData | undefined {
  return lookupParam(_params, name);
}

/** Resolve an alias object ({ w: 'main:width' }) to its params ({ w: param }). */
function _resolve<T extends ParamAliases>(aliases: T): ResolvedParams<T> {
  const out: Record<string, ParamData | undefined> = {};
  for (const [alias, name] of Object.entries(aliases)) out[alias] = _get(name);
  return out as ResolvedParams<T>;
}

// ============================================================================
// The `params` namespace — read them and react to them
// ============================================================================

/**
 * React to named params changing. One name, an array of them, or an alias
 * object; the callback gets the matching shape back.
 *
 * Does NOT fire immediately — it reports later changes. Subscribing before
 * init() is what makes that a non-issue: the configurator publishes the table
 * the moment it binds, so that first publish wakes you and there is no current
 * value to go and fetch. Subscribe after init() and you have missed it. Use
 * `bindParams()` (components/base.ts) to bring an element up to date straight
 * away. Fires once per publish however many of the named params changed
 * together.
 *
 * A standalone function rather than a method on the object below because an
 * object literal can't carry TS overload signatures.
 */
function _subscribe(name: string, cb: (param: ParamData | undefined, name: string) => void): () => void;
function _subscribe(names: readonly string[], cb: (params: Array<ParamData | undefined>, names: readonly string[]) => void): () => void;
function _subscribe<T extends ParamAliases>(aliases: T, cb: (params: ResolvedParams<T>) => void): () => void;
function _subscribe(target: any, cb: any): () => void {
  return _source.subscribe(target, cb);
}

/**
 * The live Paramate param table.
 *
 *   params.get('width')                                   // one param, prefix optional
 *   params.getVal('width', 0)                             // just its value
 *   params.all()                                          // the whole table
 *   params.subscribe('main:width', (p, name) => ...)      // react to one
 *   params.subscribe(['w', 'h'], ([w, h]) => ...)         // react to several
 *   params.subscribe({ w: 'main:width' }, ({ w }) => ...) // react to a named set
 *   params.subscribeAll((table) => ...)                   // react to any publish
 *
 * Read-only. Writing still goes through `<as-configurator>.setParamValue()`,
 * which owns pending state, undo history and the input registry.
 *
 * The same `subscribe` / `subscribeAll` pair is on `appData` and `sysData`.
 */
export const params = {
  /** One param by name, with or without the 'main:' prefix. undefined before
   *  the configurator has initialised, or for a name that doesn't exist. */
  get(name: string): ParamData | undefined {
    return _get(name);
  },

  /** The value of one param, or `fallback` when the param is missing or has no
   *  value yet. Shorthand for the `params.get(name)?.value ?? fallback` that
   *  most reads come down to. */
  getVal<T = any>(name: string, fallback?: T): T {
    return (_get(name)?.value ?? fallback) as T;
  },

  /** The param table as of the last publish. The same object the configurator
   *  holds, so treat it as read-only. */
  all(): Record<string, ParamData> {
    return _params;
  },

  /** Resolve an alias object ({ w: 'main:width' }) to its params ({ w: param }). */
  resolve<T extends ParamAliases>(aliases: T): ResolvedParams<T> {
    return _resolve(aliases);
  },

  subscribe: _subscribe,

  /** React to any publish, with the whole table. Fires once per publish even
   *  when nothing in it changed — something downstream may be counting
   *  configures rather than watching values. */
  subscribeAll(cb: (params: Record<string, ParamData>) => void): () => void {
    return _source.subscribe(cb);
  },
};

// ============================================================================
// Publishing (called by <as-configurator>)
// ============================================================================

/**
 * Hand the param store a new param table. Works out which params actually
 * changed, notifies only those, then notifies whole-table subscribers and
 * dispatches the long-standing 'as-paramate-params-updated' window event so
 * existing listeners keep working.
 *
 * Called from every point where the table can change: after configure, after
 * restore, after a project load, after a file upload, and once per microtask
 * after staged writes. A param with `dirty` set is staged and not yet
 * configured.
 */
export function publishParams(params: Record<string, ParamData> | null | undefined): void {
  const next = params ?? {};

  const changed: string[] = [];
  for (const key of new Set([..._signatures.keys(), ...Object.keys(next)])) {
    const signature = _signature(next[key]);
    if (_signatures.get(key) === signature) continue;
    if (key in next) _signatures.set(key, signature);
    else _signatures.delete(key);
    changed.push(key);
  }

  _params = next;

  // Unconditional, even when nothing changed: whole-table subscribers have
  // always fired once per publish, and something downstream may be counting
  // configures rather than watching values.
  _source.commit(changed.flatMap(_aliasKeys));

  sendEv(window, 'as-paramate-params-updated', { detail: { params: next } });
}

/** Drop the snapshot. For tests and for teardown paths that end a session
 *  without a replacement table to publish. Subscribers stay registered. */
export function clearParams(): void {
  publishParams({});
}
